import { useEffect, useMemo, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { DashboardPageFormBody, DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import { Button, RadioGroup, RadioGroupItem, Switch, Textarea } from '@shigo-ui/ui';

import { showRequestError } from '../../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../../lib/messages';
import {
  useEndpointExchangeMacros,
  useGenerateExchangeConnectionMutation,
} from '../../../../../supply/endpointGenerator/hooks';
import { EndpointMacroDialog } from '../../../../../supply/endpointGenerator/components/EndpointMacroDialog/EndpointMacroDialog';
import {
  fallbackIntegrationBidderConfig,
  resolveIntegrationCustomParameters,
} from '../../../integrationConnectionConfig';
import {
  useDemandPartnerBidderConfigQuery,
  useDemandPartnerOptionsQuery,
} from '../../../../partners/hooks';
import { IntegrationCustomParameterField, IntegrationType, type Integration } from '../../../model';
import type { IntegrationFormValue } from '../../../integrationFormSchema';
import {
  IntegrationCustomParameterInput,
  IntegrationNumberField,
} from './IntegrationConnectionInputs';
import { integrationNumericFieldHint } from './IntegrationConnectionFields.utils';

const integrationTypeLabels = {
  [IntegrationType.OpenRtb]: 'oRTB',
  [IntegrationType.Vast]: 'VAST',
  [IntegrationType.PrebidServer]: 'PreBid server',
  [IntegrationType.Custom]: 'Custom',
} as const;

const numericFields = [
  {
    name: 'targetCpm',
    label: 'integrations.fixedNetCpm',
    prefix: '$',
  },
  {
    name: 'bidFloor',
    label: 'integrations.bidFloor',
    prefix: '$',
  },
  {
    name: 'qps',
    label: 'integrations.qpsLimit',
    max: 5787,
  },
  {
    name: 'shareOfVoice',
    label: 'integrations.shareOfVoice',
    suffix: '%',
    max: 100,
  },
] as const;

export function IntegrationConnectionFields(props: {
  initialIntegration?: Integration | undefined;
}) {
  const { control, register, formState, setValue } = useFormContext<IntegrationFormValue>();
  const integrationType = useWatch({ control, name: 'integrationType' });
  const demandPartnerId = useWatch({ control, name: 'demandPartnerId' });
  const endpointUri = useWatch({ control, name: 'endpointUri' });
  const qps = useWatch({ control, name: 'qps' });
  const shareOfVoice = useWatch({ control, name: 'shareOfVoice' });
  const partners = useDemandPartnerOptionsQuery('', demandPartnerId);
  const bidderConfig = useDemandPartnerBidderConfigQuery();
  const bidderCode =
    partners.data?.content.find(({ id }) => id === demandPartnerId)?.bidderCode ??
    props.initialIntegration?.demandPartner.bidderCode;
  const customParameters = useMemo(
    () =>
      resolveIntegrationCustomParameters(
        bidderCode,
        bidderConfig.data ?? fallbackIntegrationBidderConfig,
      ),
    [bidderCode, bidderConfig.data],
  );
  const requiredCustomParameterFields = useMemo(
    () => customParameters.filter(({ required }) => required).map(({ field }) => field),
    [customParameters],
  );
  const vast = integrationType === IntegrationType.Vast;
  const macros = useEndpointExchangeMacros(vast);
  const autoReplace = useGenerateExchangeConnectionMutation();
  const [macroDialogOpen, setMacroDialogOpen] = useState(false);

  useEffect(() => {
    setValue('requiredCustomParameterFields', requiredCustomParameterFields, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [requiredCustomParameterFields, setValue]);
  let customParametersContent;
  if (!bidderCode) {
    customParametersContent = (
      <p className="border-border bg-muted/30 rounded-md border px-4 py-3 text-sm">
        {dashboardT('integrations.selectDemandPartnerForCustomParameters')}
      </p>
    );
  } else if (customParameters.length) {
    customParametersContent = (
      <div className="grid gap-5 md:grid-cols-2">
        {customParameters.map(({ field, required }) => (
          <div
            key={field}
            className={
              field === IntegrationCustomParameterField.ImpExt ? 'md:col-span-2' : undefined
            }
          >
            <IntegrationCustomParameterInput field={field} required={required} />
          </div>
        ))}
      </div>
    );
  } else {
    customParametersContent = (
      <p className="text-muted-foreground text-sm">
        {dashboardT('integrations.noCustomParameters')}
      </p>
    );
  }

  return (
    <DashboardPageFormBody width="lg" title={dashboardT('integrations.tabs.integration')}>
      <section className="grid gap-5">
        <div>
          <h3 className="text-sm font-semibold">
            {dashboardT('integrations.sections.preferences')}
          </h3>
        </div>
        <Controller
          control={control}
          name="integrationType"
          render={({ field }) => (
            <DashboardPageFormField label={dashboardT('integrations.type')} required>
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                className="flex flex-wrap gap-x-6 gap-y-3"
              >
                {Object.values(IntegrationType).map((type) => (
                  <label key={type} className="flex cursor-pointer items-center gap-2 text-sm">
                    <RadioGroupItem value={type} />
                    {integrationTypeLabels[type]}
                  </label>
                ))}
              </RadioGroup>
            </DashboardPageFormField>
          )}
        />
        <DashboardPageFormField
          label={dashboardT('integrations.endpointTag', {
            type: integrationTypeLabels[integrationType],
          })}
          htmlFor="integration-endpointUri"
          required
          error={formState.errors.endpointUri?.message}
        >
          <Textarea
            id="integration-endpointUri"
            rows={5}
            required
            aria-invalid={Boolean(formState.errors.endpointUri)}
            {...register('endpointUri')}
          />
        </DashboardPageFormField>
        {vast ? (
          <div>
            <Button
              type="button"
              disabled={!endpointUri.trim() || !demandPartnerId || macros.isLoading}
              onClick={() => {
                setMacroDialogOpen(true);
              }}
            >
              <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
              {dashboardT('integrations.editWithMacros')}
            </Button>
          </div>
        ) : null}
      </section>

      <section className="border-border grid gap-5 border-t pt-5">
        <div>
          <h3 className="text-sm font-semibold">{dashboardT('integrations.sections.delivery')}</h3>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          {numericFields.map((field) => {
            const hint = integrationNumericFieldHint(field.name, shareOfVoice);
            return (
              <IntegrationNumberField
                key={field.name}
                name={field.name}
                label={dashboardT(field.label)}
                prefix={'prefix' in field ? field.prefix : undefined}
                suffix={'suffix' in field ? field.suffix : undefined}
                max={'max' in field ? field.max : undefined}
                hint={hint}
              />
            );
          })}
        </div>
        {qps === 0 ? (
          <p role="alert" className="text-destructive text-sm">
            {dashboardT('integrations.hints.qpsZero')}
          </p>
        ) : null}
        <Controller
          control={control}
          name="gzip"
          render={({ field }) => (
            <label className="border-input hover:bg-muted/30 flex min-h-11 cursor-pointer items-center justify-between gap-4 rounded-md border px-4 py-2.5 text-sm transition-colors">
              <span>{dashboardT('integrations.gzipCompression')}</span>
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            </label>
          )}
        />
      </section>

      <section className="border-border grid gap-4 border-t pt-5">
        <div>
          <h3 className="text-sm font-semibold">{dashboardT('integrations.timeout')}</h3>
          <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
            {dashboardT('integrations.hints.biddingTmax')}
          </p>
        </div>
        <div className="max-w-xl">
          <IntegrationNumberField name="biddingTmax" label={dashboardT('integrations.timeout')} />
        </div>
      </section>

      <section className="border-border grid gap-4 border-t pt-5">
        <div>
          <h3 className="text-sm font-semibold">
            {dashboardT('integrations.sections.customParameters')}
          </h3>
        </div>
        {customParametersContent}
      </section>

      {macroDialogOpen ? (
        <EndpointMacroDialog
          endpoint={endpointUri}
          macros={macros.data ?? []}
          autoReplaceDisabled={!bidderCode}
          onAutoReplace={async (endpoint) => {
            try {
              return await autoReplace.mutateAsync({
                endpoint,
                source: bidderCode ?? '',
              });
            } catch (error) {
              showRequestError(error, dashboardT('integrations.macroReplaceError'));
              throw new Error('Integration endpoint macro replacement failed.');
            }
          }}
          onApply={(nextEndpoint) => {
            setValue('endpointUri', nextEndpoint, {
              shouldDirty: true,
              shouldValidate: true,
            });
            setMacroDialogOpen(false);
          }}
          onClose={() => {
            setMacroDialogOpen(false);
          }}
        />
      ) : null}
    </DashboardPageFormBody>
  );
}
